import { Injectable } from '@angular/core';
import { Response } from '../models/response';
import { EmotionOption } from '../models/emotionOptionEnum';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import * as XLSX from 'xlsx';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  participantID: string | undefined = undefined;

  responses: Response[] = [];

  currentResponse: Response | null = null;

  classificationStart: any;
  intensityRatingStart: any;

  constructor(
    private dbService: NgxIndexedDBService,
    private http: HttpClient
  ) {}

  resetData() {
    this.responses = [];
  }

  startCountingClassification() {
    console.log('Classification start');
    this.classificationStart = performance.now();
  }

  startCountingIntensityRating() {
    this.intensityRatingStart = performance.now();
  }

  createNewResponse(trialNumber: number, correctEmotion: EmotionOption) {
    this.currentResponse = {
      trialNumber,
      emotionClassificationResp: null,
      emotionClassificationRespTime: null,
      intensityResp: null,
      intensityRespTime: null,
      correctEmotion,
      isClassificationCorrect: null,
    };
  }

  saveEmotionClassificationResp(resp: EmotionOption) {
    if (this.currentResponse !== null) {
      let responseTime = Number(
        ((performance.now() - this.classificationStart) / 1000).toPrecision(3)
      );
      this.currentResponse.emotionClassificationResp = resp;
      this.currentResponse.emotionClassificationRespTime = responseTime;
      this.currentResponse.isClassificationCorrect =
        resp === this.currentResponse.correctEmotion ? 1 : 0;
    }
  }

  getCurrentEmotionClassificationResponseTime() {
    return Number(
      ((performance.now() - this.classificationStart) / 1000).toPrecision(3)
    );
  }

  saveIntensityResp(resp: number) {
    if (this.currentResponse !== null) {
      let responseTime = Number(
        ((performance.now() - this.intensityRatingStart) / 1000).toPrecision(3)
      );
      this.currentResponse.intensityResp = resp;
      this.currentResponse.intensityRespTime = responseTime;
    }
  }

  saveNeutralIntensityResp() {
    if (this.currentResponse !== null) {
      this.currentResponse.intensityResp = -1;
      this.currentResponse.intensityRespTime = -1;
    }
  }

  getCurrentIntensityResponseTime() {
    return Number(
      ((performance.now() - this.intensityRatingStart) / 1000).toPrecision(3)
    );
  }

  async saveResp(
    participantIdentifier: string | undefined,
    trialSequenceNumber: number | undefined
  ) {
    if (
      this.currentResponse === null ||
      this.currentResponse.emotionClassificationResp === null ||
      this.currentResponse.emotionClassificationRespTime === null
    ) {
      throw new Error('response not completed');
    } else {
      // Do not save response and update trial if response has been saved before
      if (
        !this.responses.find(
          (x) => x.trialNumber == this.currentResponse?.trialNumber
        )
      ) {
        this.responses.push(this.currentResponse);
        if (!participantIdentifier) {
          alert(
            'Error in saving responses - Cannot find the participant identifier'
          );
        } else {
          await firstValueFrom(
            this.dbService.update('responses', {
              participantIdentifier: participantIdentifier,
              trialSequenceNumber: trialSequenceNumber,
              taskData: this.responses,
            })
          );
        }
      }
    }
  }

  async loadParticipantResponses(
    participantIdentifier: any,
    currentTrialNumber: any,
    correctEmotion: EmotionOption
  ) {
    if (currentTrialNumber === 1) {
      this.responses = [];
    } else {
      const participantResponses: any = await lastValueFrom(
        this.dbService.getByKey('responses', participantIdentifier)
      );
      const responses: any = participantResponses.taskData;
      if (
        Number(responses[responses.length - 1].trialNumber) !==
        currentTrialNumber - 1
      ) {
        throw new Error(
          'Error in loading participant responses - mismatch trial number'
        );
      } else {
        this.responses = responses;
      }
    }
    this.createNewResponse(currentTrialNumber, correctEmotion);
  }

  /**
   * Export responses data as a CSV file for download
   * @param participantResponses Array of participant responses, each containing participantIdentifier and taskData (responses)
   * @param trialSequenceNumber The trial sequence number (all responses should have the same trial sequence number)
   */
  async exportResponsesAsExcel(
    participantResponses: any[],
    trialSequenceNumber: number
  ) {
    try {
      // Load trial sequence data to get trial images
      const trialData: any = await firstValueFrom(
        this.http.get(`assets/data/trial-sequence-${trialSequenceNumber}.json`)
      );

      const trialsImages = trialData.trialsImages;
      const numberOfTrials = trialsImages.length;

      // Helper function to convert emotion enum value to key
      const getEmotionKey = (emotionValue: EmotionOption | null): string => {
        if (!emotionValue) return '';

        // Find the key that matches the value
        for (const [key, value] of Object.entries(EmotionOption)) {
          if (value === emotionValue) {
            return key;
          }
        }
        return '';
      };

      // Helper function to extract intensity range from image filename
      const getIntensityRange = (imageFilename: string): number => {
        // Extract the last digit before the extension
        const match = imageFilename.match(/_(\d)\.png$/);
        return match ? parseInt(match[1]) : -1;
      };

      // Helper function to determine if intensity response is correct
      const isIntensityCorrect = (responseIntensity: number | null, intensityRange: number): number => {
        if (responseIntensity === null || responseIntensity === undefined) return 0;
        
        // Check if response intensity matches the expected range
        if (intensityRange === 1 && responseIntensity >= 1 && responseIntensity <= 3) return 1;
        if (intensityRange === 2 && responseIntensity >= 4 && responseIntensity <= 6) return 1;
        if (intensityRange === 3 && responseIntensity >= 7 && responseIntensity <= 9) return 1;
        
        return 0;
      };

      // Helper function to check if trial is neutral based on filename
      const isNeutralTrial = (imageFilename: string): boolean => {
        return imageFilename.includes('Neutral');
      };

      // Create column headers
      const headers = ['Participant ID', 'Uploaded By'];

      // Add columns for each trial (7 columns for neutral trials, 9 columns for non-neutral trials)
      for (let i = 0; i < numberOfTrials; i++) {
        const trialNumber = i + 1;
        const trialImage = trialsImages[i];
        const imageFilename = trialImage.split('/').pop() || trialImage;
        const isNeutral = isNeutralTrial(imageFilename);

        headers.push(`${trialNumber}_image_filename`);
        headers.push(`${trialNumber}_emotion_resp`);
        headers.push(`${trialNumber}_emotion_rt`);
        headers.push(`${trialNumber}_intensity_resp`);
        headers.push(`${trialNumber}_intensity_rt`);
        headers.push(`${trialNumber}_emotion`);
        headers.push(`${trialNumber}_emotion_resp_correct`);
        
        // Only add intensity columns for non-neutral trials
        if (!isNeutral) {
          headers.push(`${trialNumber}_intensity_range`);
          headers.push(`${trialNumber}_intensity_correct`);
        }
      }

      // Create data rows
      const rows = [];

      for (const participantData of participantResponses) {
        const row = [participantData.participantIdentifier];
        row.push(participantData.uploadedBy);
        const responses = participantData.taskData as Response[];

        // Create a map for quick lookup of responses by trial number
        const responseMap = new Map<number, Response>();
        responses.forEach((response) => {
          responseMap.set(response.trialNumber, response);
        });

        // Add data for each trial
        for (let i = 0; i < numberOfTrials; i++) {
          const trialNumber = i + 1;
          const response = responseMap.get(trialNumber);
          const trialImage = trialsImages[i];

          // Extract filename without folder prefix
          const imageFilename = trialImage.split('/').pop() || trialImage;
          
          // Check if this trial is neutral
          const isNeutral = isNeutralTrial(imageFilename);
          
          // Extract intensity range from image filename (only needed for non-neutral)
          const intensityRange = !isNeutral ? getIntensityRange(imageFilename) : -1;

          if (response) {
            // image_filename: filename without folder prefix
            row.push(imageFilename);
            // emotion_resp: emotionClassificationResp as enum key
            row.push(getEmotionKey(response.emotionClassificationResp));
            // emotion_rt: emotionClassificationRespTime
            row.push(response.emotionClassificationRespTime || '');
            // intensity_resp: intensityResp
            row.push(response.intensityResp || '');
            // intensity_rt: intensityRespTime
            row.push(response.intensityRespTime || '');
            // emotion: correctEmotion as enum key
            row.push(getEmotionKey(response.correctEmotion));
            // emotion_resp_correct: 1 if correct, 0 if wrong
            row.push(
              response.isClassificationCorrect !== null
                ? response.isClassificationCorrect
                : ''
            );
            
            // Only add intensity columns for non-neutral trials
            if (!isNeutral) {
              // intensity_range: extracted from image filename
              row.push(intensityRange);
              // intensity_correct: 1 if correct, 0 if wrong
              row.push(isIntensityCorrect(response.intensityResp, intensityRange));
            }
          } else {
            // If no response for this trial, fill with empty values
            row.push(imageFilename, '', '', '', '', '', '');
            
            // Only add intensity columns for non-neutral trials
            if (!isNeutral) {
              row.push(intensityRange, '');
            }
          }
        }

        rows.push(row);
      }

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

      // Generate filename with timestamp
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, '-')
        .slice(0, -5);
      const filename = `hkdef_responses_sequence${trialSequenceNumber}_${timestamp}.csv`;

      // Convert to CSV and download
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error exporting responses to CSV:', error);
      throw new Error('Failed to export responses to CSV file');
    }
  }
}
